import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import {
  createCompanyUser,
  deleteCompanyUser,
  getCompanyUserCatalog,
  listCompanyUsers,
  updateCompanyUser,
} from '../../api/companyUsers';
import './CompanyUsersPage.css';

// Parametres > Utilisateurs : autres personnes qui se connectent sur le MEME
// ecran de login que la company, avec leur propre username/mot de passe, et
// arrivent dans cet espace. Toutes les pages sont accordees par defaut — on
// decoche ce qu'on veut leur retirer (bloque aussi cote serveur, cf. backend
// preloteserver/company_user_access.py). Nombre illimite.
const emptyForm = { name: '', username: '', password: '', is_active: true, denied_keys: [] };

const errorMessage = (err, fallback) => {
  try {
    const parsed = JSON.parse(err.message);
    const detail = Object.values(parsed).flat().join(' ');
    if (detail) return `${fallback} — ${detail}`;
  } catch (_) {
    if (err.message) return `${fallback} — ${err.message}`;
  }
  return fallback;
};

const CompanyUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = pas de formulaire ouvert
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    Promise.all([listCompanyUsers(), getCompanyUserCatalog()])
      .then(([usersData, catalogData]) => {
        setUsers(usersData);
        setCatalog(catalogData);
      })
      .catch(() => setError('Impossible de charger les utilisateurs.'))
      .finally(() => setLoading(false));
  }, []);

  const deniedSet = useMemo(() => new Set(form.denied_keys), [form.denied_keys]);

  const openCreate = () => {
    setEditingId('new');
    setForm(emptyForm);
    setError('');
  };

  const openEdit = (user) => {
    setEditingId(user.id);
    setForm({
      name: user.name || '',
      username: user.username,
      password: '',
      is_active: user.is_active,
      denied_keys: user.denied_keys || [],
    });
    setError('');
  };

  const closeForm = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  // Case cochee = page accordee (absente de denied_keys).
  const toggleKey = (key) => {
    setForm((prev) => {
      const set = new Set(prev.denied_keys);
      if (set.has(key)) set.delete(key);
      else set.add(key);
      return { ...prev, denied_keys: Array.from(set) };
    });
  };

  const toggleSection = (items, grant) => {
    setForm((prev) => {
      const set = new Set(prev.denied_keys);
      items.forEach((it) => (grant ? set.delete(it.key) : set.add(it.key)));
      return { ...prev, denied_keys: Array.from(set) };
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        name: form.name,
        username: form.username,
        is_active: form.is_active,
        denied_keys: form.denied_keys,
      };
      if (form.password) payload.password = form.password;
      if (editingId === 'new') {
        const created = await createCompanyUser(payload);
        setUsers((prev) => [...prev, created]);
      } else {
        const updated = await updateCompanyUser(editingId, payload);
        setUsers((prev) => prev.map((u) => (u.id === editingId ? updated : u)));
      }
      closeForm();
    } catch (err) {
      setError(errorMessage(err, 'Sauvegarde impossible.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Supprimer l'utilisateur "${user.username}" ?`)) return;
    setError('');
    try {
      await deleteCompanyUser(user.id);
      setUsers((prev) => prev.filter((u) => u.id !== user.id));
      if (editingId === user.id) closeForm();
    } catch (_) {
      setError('Suppression impossible pour cet utilisateur.');
    }
  };

  return (
    <Layout>
      <p className="cu-breadcrumb">
        Ruta : <Link to="/dashboard" className="bc-link">Menu</Link>
        {' >> '}
        <Link to="/parametres" className="bc-link">Parametres</Link>
        {' >> '}
        <span className="bc-current">Utilisateurs</span>
      </p>

      <h2 className="cu-title">Utilisateurs</h2>
      <p className="cu-hint">
        Ces personnes se connectent sur la meme page de connexion que la company, avec leur
        propre nom d&apos;utilisateur et mot de passe. Toutes les pages sont autorisees par
        defaut : decochez celles que vous voulez leur retirer. Un utilisateur ne peut jamais
        gerer les autres utilisateurs.
      </p>

      {error && <p className="cu-error">{error}</p>}

      {editingId === null && (
        <button type="button" className="cu-new-btn" onClick={openCreate}>
          + Nouvel utilisateur
        </button>
      )}

      {editingId !== null && (
        <form onSubmit={handleSave} className="cu-form">
          <h3 className="cu-form-title">
            {editingId === 'new' ? 'Nouvel utilisateur' : `Modifier : ${form.username}`}
          </h3>

          <div className="cu-form-row">
            <label className="cu-label">
              Nom
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              />
            </label>
            <label className="cu-label">
              Nom d&apos;utilisateur
              <input
                type="text"
                value={form.username}
                onChange={(e) => setForm((p) => ({ ...p, username: e.target.value }))}
                required
              />
            </label>
            <label className="cu-label">
              {editingId === 'new' ? 'Mot de passe' : 'Nouveau mot de passe (optionnel)'}
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                required={editingId === 'new'}
                minLength={6}
              />
            </label>
            <label className="cu-label cu-checkbox-label">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
              />
              Actif
            </label>
          </div>

          <div className="cu-catalog">
            {catalog.map((section) => (
              <div key={section.section} className="cu-section">
                <div className="cu-section-header">
                  <strong>{section.section}</strong>
                  <span className="cu-section-actions">
                    <button type="button" onClick={() => toggleSection(section.items, true)}>
                      Tout cocher
                    </button>
                    <button type="button" onClick={() => toggleSection(section.items, false)}>
                      Tout decocher
                    </button>
                  </span>
                </div>
                <div className="cu-section-items">
                  {section.items.map((item) => (
                    <label key={item.key} className="cu-item-checkbox">
                      <input
                        type="checkbox"
                        checked={!deniedSet.has(item.key)}
                        onChange={() => toggleKey(item.key)}
                      />
                      {item.label}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="cu-form-actions">
            <button type="submit" className="cu-save-btn" disabled={saving}>
              {saving ? 'Sauvegarde...' : 'Sauvegarder'}
            </button>
            <button type="button" className="cu-cancel-btn" onClick={closeForm} disabled={saving}>
              Annuler
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p>Chargement...</p>
      ) : (
        <div className="cu-table-wrap">
          <table className="cu-table">
            <thead>
              <tr>
                <th>Nom</th>
                <th>Utilisateur</th>
                <th>Statut</th>
                <th>Acces</th>
                <th>Derniere connexion</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>{user.name || '—'}</td>
                  <td>{user.username}</td>
                  <td>{user.is_active ? 'Actif' : 'Inactif'}</td>
                  <td>
                    {(user.denied_keys || []).length === 0 ? (
                      'Tout'
                    ) : (
                      <span className="cu-denied">
                        Tout sauf {user.denied_keys.length} page(s)
                      </span>
                    )}
                  </td>
                  <td>{user.last_login ? new Date(user.last_login).toLocaleString() : '—'}</td>
                  <td>
                    <div className="cu-actions">
                      <button type="button" className="cu-edit-btn" onClick={() => openEdit(user)}>
                        Modifier
                      </button>
                      <button type="button" className="cu-delete-btn" onClick={() => handleDelete(user)}>
                        Supprimer
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={6} className="cu-empty">Aucun utilisateur cree.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </Layout>
  );
};

export default CompanyUsersPage;
